import React, { FunctionComponent } from 'react';
import { useLocale } from '../../contexts/LocaleProvider';
import { IntlProvider } from 'react-intl';
import { Box } from '@mui/material';
import ReusableTabs from '../common/ReusableTabs';

import * as styles from '../productPage/tableComponents/ProductPage_style';
import TelecommunicationTemplate from './TelecommunicationTemplate';
import { PrefilteredResourceLibraryItemsProvider } from '../../graphql/pimcore/modules/resourceLibrary/contexts/PrefilteredResourceLibraryItemsProvider';
import ToolPage from '../resourcesPage/simulationPage/ToolPage';
import BreadCrumbs from '../common/BreadCrumbs';
import TechnicalResources from '../automotivePage/TechnicalResources';
import { useApplicationsContent } from '../../graphql/pimcore/modules/siteContent/hooks/useApplicationsContent';
import DebouncedLoader from '../common/DebouncedLoader';
import { LinkCollectionEntry } from '../../graphql/pimcore/modules/siteContent/types/SiteContentElement';
import { useSalesAndResourcesSimulationToolsContent } from '../../graphql/pimcore/modules/siteContent/hooks/useSalesAndResourcesSimulationToolsContent';

interface TelecomunicationPageProps {}

export interface Telecommunication {
    pageName: string;
    title: string;
    description: string;
    image: string;
    linkRoute: string;
    linkText: string;
}

const getLinkHref = (link?: LinkCollectionEntry): string => {
    if (!link) return '#';
    return link.kind === 'externalLinkFieldCollection' ? link.linkUrl : link.linkRoute;
};

const getLinkText = (link?: LinkCollectionEntry): string => {
    if (!link) return 'Products';
    return link.linkText;
};

const TelecomunicationPage: FunctionComponent<TelecomunicationPageProps> = () => {
    const { locale, messages } = useLocale();
    const { loading, error, contentTree } = useApplicationsContent('Telecommunications');
    const { contentTree: toolsContent } = useSalesAndResourcesSimulationToolsContent();

    if (error && !loading) {
        return <div className="mid-width">Error: {error.message}</div>;
    }

    const telecommunicationTemplates: Telecommunication[] = [
        {
            pageName: '5G',
            title: contentTree.FiveGAndWirelessInfrastructure.title.content,
            description: contentTree.FiveGAndWirelessInfrastructure.mainText.content,
            image: contentTree.FiveGAndWirelessInfrastructure.mainImage.imageUrl,
            linkRoute: getLinkHref(contentTree.FiveGAndWirelessInfrastructure.links.links[0]),
            linkText: getLinkText(contentTree.FiveGAndWirelessInfrastructure.links.links[0]),
        },
        {
            pageName: 'Fixed Network Infrastructure',
            title: contentTree.FixedNetworkInfrastructure.title.content,
            description: contentTree.FixedNetworkInfrastructure.mainText.content,
            image: contentTree.FixedNetworkInfrastructure.mainImage.imageUrl,
            linkRoute: getLinkHref(contentTree.FixedNetworkInfrastructure.links.links[0]),
            linkText: getLinkText(contentTree.FixedNetworkInfrastructure.links.links[0]),
        },
    ];

    return (
        <DebouncedLoader loading={loading} delay={1000} size={40} centered={true}>
            <IntlProvider locale={locale} key={locale} defaultLocale="en" messages={messages}>
                <Box
                    width="100%"
                    component="main"
                    sx={{
                        padding: 0,
                        flexGrow: 0,
                        p: 0, // 内边距
                    }}
                >
                    <PrefilteredResourceLibraryItemsProvider
                        initialFilters={{
                            productApplicationKeys: ['Telecommunications'],
                        }}
                    >
                        <ReusableTabs
                            bannerImage={contentTree.pageTitleBackgroundImage.imageUrl}
                            bannerTitle={contentTree.pageTitle.content}
                            bannerDescription={contentTree.pageSubtitle.content}
                            tabLabels={[
                                contentTree.tabs.Overview.title.content,
                                contentTree.tabs.Resources.title.content,
                                contentTree.tabs.SimulationTools.title.content,
                            ]}
                            tabKeys={['Overview', 'Resources', 'SimulationTools']}
                            tabContents={[
                                <TelecommunicationTemplate
                                    key="overview"
                                    telecommunications={telecommunicationTemplates}
                                />,
                                <TechnicalResources key="resources" backgroundColor={'white'} />,
                                <ToolPage
                                    key="simulation-tools"
                                    cmsContent={toolsContent.Overview}
                                />,
                            ]}
                            bannerFontStyle={styles.bannerFont}
                            subBannerFontStyle={styles.bannerFont2}
                            pageName={'telecommunications'}
                            showTabs={true}
                            breadcrumbs={
                                <BreadCrumbs navigationPath="/Home/Applications/Telecommunications" />
                            }
                        />
                    </PrefilteredResourceLibraryItemsProvider>
                </Box>
            </IntlProvider>
        </DebouncedLoader>
    );
};

export default TelecomunicationPage;
